import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const TermsOfService = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<Link to="/">
							<Logo showText={true} width={50} height={50} />
						</Link>
						<Link
							to="/"
							className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</header>

			{/* Content */}
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
					<h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
					<p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

					<div className="prose prose-lg max-w-none">
						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
							<p className="text-gray-700 mb-4">
								By accessing and using iVault ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
							</p>
							<p className="text-gray-700">
								iVault is a platform that enables startups to store, manage, and share documents with investors, and facilitates connections between startups and investors through organized pitch events and investor networks.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
							<p className="text-gray-700 mb-4">iVault provides the following services:</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Secure document storage and management for startups</li>
								<li>Document sharing capabilities with investors and investor networks</li>
								<li>Pitch deck upload and management</li>
								<li>Pitch event organization and participation</li>
								<li>Investor network access to startup profiles and documents</li>
								<li>Financial document sharing (monthly revenues, financial statements, etc.)</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>
							<h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
							<p className="text-gray-700 mb-4">
								To use iVault, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 User Roles</h3>
							<p className="text-gray-700 mb-4">
								iVault supports multiple user roles, including but not limited to:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li><strong>Startups:</strong> Can upload, store, and share documents with investors</li>
								<li><strong>Investors:</strong> Can access startup profiles, documents, and pitch decks</li>
								<li><strong>Event Organizers:</strong> Can organize and manage pitch events</li>
								<li><strong>Administrators:</strong> Can manage platform settings and users</li>
							</ul>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Account Security</h3>
							<p className="text-gray-700 mb-4">
								You are responsible for maintaining the security of your account. You must immediately notify us of any unauthorized use of your account or any other breach of security.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
							<p className="text-gray-700 mb-4">You agree to use iVault only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Upload, share, or transmit any content that is illegal, harmful, threatening, abusive, or violates any laws</li>
								<li>Upload documents containing false, misleading, or fraudulent information</li>
								<li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
								<li>Attempt to gain unauthorized access to any portion of the Service or other accounts</li>
								<li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
								<li>Use the Service to transmit viruses, malware, or other harmful code</li>
								<li>Reverse engineer, decompile, or disassemble any part of the Service</li>
								<li>Use automated systems (bots, scrapers) to access the Service without permission</li>
								<li>Share your account credentials with others</li>
								<li>Use the Service for any commercial purpose not explicitly permitted</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Content and Intellectual Property</h2>
							
							<h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Your Content</h3>
							<p className="text-gray-700 mb-4">
								You retain all ownership rights to the documents and content you upload to iVault. By uploading content, you grant iVault a limited, non-exclusive license to store, process, and display your content solely for the purpose of providing the Service.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Content Responsibility</h3>
							<p className="text-gray-700 mb-4">
								You are solely responsible for the content you upload and share. You represent and warrant that:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>You have the right to upload and share the content</li>
								<li>The content does not infringe on any third-party rights</li>
								<li>The content complies with all applicable laws and regulations</li>
								<li>The content is accurate and not misleading</li>
							</ul>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Our Intellectual Property</h3>
							<p className="text-gray-700 mb-4">
								The Service, including its design, features, and functionality, is owned by iVault and its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not use our trademarks, logos, or other proprietary information without our prior written consent.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Document Sharing and Access Control</h2>
							<p className="text-gray-700 mb-4">
								You have control over who can access your documents. However, you acknowledge that:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Once you share a document with an investor or investor network, they may download or save copies</li>
								<li>We cannot guarantee that shared documents will not be further distributed by recipients</li>
								<li>You are responsible for setting appropriate sharing permissions</li>
								<li>We reserve the right to access your content if required by law or to enforce these Terms</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Payment and Billing</h2>
							<h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Subscription Fees</h3>
							<p className="text-gray-700 mb-4">
								Certain features of iVault may require payment of subscription fees. You agree to pay all fees associated with your subscription plan as described on our pricing page.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 Billing</h3>
							<p className="text-gray-700 mb-4">
								Fees are billed in advance on a monthly or annual basis, as applicable. All fees are non-refundable except as required by law or as explicitly stated in our refund policy.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">7.3 Cancellation</h3>
							<p className="text-gray-700 mb-4">
								You may cancel your subscription at any time. Cancellation will take effect at the end of your current billing period. You will continue to have access to the Service until the end of the billing period.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy and Data Protection</h2>
							<p className="text-gray-700 mb-4">
								Your use of iVault is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect your information.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers and Limitations of Liability</h2>
							<h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Service Availability</h3>
							<p className="text-gray-700 mb-4">
								We strive to provide reliable service but do not guarantee that the Service will be available at all times, uninterrupted, or error-free. The Service is provided "as is" and "as available."
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Content Accuracy</h3>
							<p className="text-gray-700 mb-4">
								We do not verify, endorse, or guarantee the accuracy, completeness, or reliability of any content uploaded by users. You use shared content at your own risk.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">9.3 Limitation of Liability</h3>
							<p className="text-gray-700 mb-4">
								To the maximum extent permitted by law, iVault and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
							<p className="text-gray-700 mb-4">
								You agree to indemnify, defend, and hold harmless iVault, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including legal fees) arising out of or in any way connected with:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Your use of the Service</li>
								<li>Your violation of these Terms</li>
								<li>Your violation of any third-party rights</li>
								<li>Any content you upload or share</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
							<h3 className="text-xl font-semibold text-gray-900 mb-3">11.1 Termination by You</h3>
							<p className="text-gray-700 mb-4">
								You may terminate your account at any time by contacting us or using the account deletion feature in your settings.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">11.2 Termination by Us</h3>
							<p className="text-gray-700 mb-4">
								We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent or illegal activity, or for any other reason we deem necessary to protect the Service or other users.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">11.3 Effect of Termination</h3>
							<p className="text-gray-700 mb-4">
								Upon termination, your right to use the Service will immediately cease. We may delete your account and content, subject to our data retention policies and legal obligations.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
							<p className="text-gray-700 mb-4">
								We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the modified Terms.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law and Dispute Resolution</h2>
							<p className="text-gray-700 mb-4">
								These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration or in the courts of [Your Jurisdiction].
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
							<p className="text-gray-700 mb-4">
								If you have any questions about these Terms of Service, please contact us:
							</p>
							<div className="bg-gray-50 p-4 rounded-lg">
								<p className="text-gray-700"><strong>Email:</strong> legal@ivault.com</p>
								<p className="text-gray-700"><strong>Address:</strong> Techpuram, [Your Address]</p>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Severability</h2>
							<p className="text-gray-700 mb-4">
								If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
							</p>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TermsOfService;

